import {
  add,
  always,
  concat,
  converge,
  curry,
  flip,
  map,
  mergeRight,
  multiply,
  nthArg,
  objOf,
  pipe,
  prop,
  range,
  reduce,
  sort,
  subtract,
  unapply,
  useWith,
} from 'ramda'
import {createEdgesFromPoints} from './common'

const JITTER = 0.0025
const LINEWIDTH_MIN = 2.5
const LINEWIDTH_MAX = 3.5
const SEGMENTS_MIN = 1
const SEGMENTS_MAX = 40

export default function line(props) {
  const segments = createSegments(props)

  /**
   * @param context {CanvasRenderingContext2D}
   */
  return ({context, width, height}) => {
    const prevLineWidth = context.lineWidth

    segments.forEach((props) => {
      const {x1, y1, x2, y2, lineWidth} = props
      context.lineWidth = lineWidth
      context.beginPath()
      context.moveTo(x1 * width, y1 * height)
      context.lineTo(x2 * width, y2 * height)
      context.stroke()
    })
    context.lineWidth = prevLineWidth
  }
}

function rand(min, max) {
  return min + (Math.random() * (max - min))
}

const randRange = useWith(unapply(curry(converge)(rand)), [always, always])

const square = flip(Math.pow)(2)

const lineLength = converge(pipe(add, Math.sqrt), [
  converge(pipe(subtract, square), [prop('x1'), prop('x2')]),
  converge(pipe(subtract, square), [prop('y1'), prop('y2')]),
])

const createSegmentCount = pipe(
  lineLength,
  converge(rand, [pipe(multiply(SEGMENTS_MIN)), pipe(multiply(SEGMENTS_MAX))]),
)

const rangeFromCount = range(0)

const toRandomPosition = randRange(0, 1)

const sortAscending = sort(subtract)

const addRangeBounds = pipe(concat([0]), flip(concat)([1]))

const jitter = randRange(JITTER * -1, JITTER)

const toSegmentPoint = converge(unapply(reduce(mergeRight, {})), [
  objOf('position'),
  pipe(randRange(LINEWIDTH_MIN, LINEWIDTH_MAX), objOf('lineWidth')),
  pipe(jitter, objOf('jitterX')),
  pipe(jitter, objOf('jitterY')),
])

const createRandomSegments = pipe(
  createSegmentCount,
  rangeFromCount,
  map(toRandomPosition),
  sortAscending,
  addRangeBounds,
  map(toSegmentPoint),
  createEdgesFromPoints,
)

const createSegments = converge(map, [
  createSegment,
  createRandomSegments,
])

const createScaleFn = converge(pipe, [
  pipe(flip(subtract), multiply),
  pipe(nthArg(0), add),
])

function createSegment(props) {
  const {x1, y1, x2, y2} = props

  const scaleX = createScaleFn(x1, x2)
  const scaleY = createScaleFn(y1, y2)

  return ([point1, point2]) => {
    return ({
      x1: scaleX(point1.position) + point1.jitterX,
      y1: scaleY(point1.position) + point1.jitterY,
      x2: scaleX(point2.position) + point2.jitterX,
      y2: scaleY(point2.position) + point1.jitterY,
      lineWidth: point1.lineWidth,
    })
  }
}